import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchSectionById, saveSection, fetchSections } from "@/api/sections";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Save,
  LayoutPanelTop,
  ImageIcon,
  BookOpen,
  BookMarked,
  LibrarySquare,
  Users,
  Eye,
  EyeOff,
  MoveVertical,
} from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Section, SectionType } from "@/types";
import React from "react";

// Validation schema
const formSchema = z.object({
  title: z.string().min(2, "Title must be at least 2 characters"),
  subtitle: z.string().optional(),
  description: z.string().optional(),
  type: z.enum([
    "banner",
    "category",
    "book_summary",
    "collection",
    "people",
  ] as const),
  position: z.coerce.number().int().default(0),
  isActive: z.boolean().default(true),
  displayStyle: z.string().optional(),
  maxItems: z.coerce.number().int().optional(),
});

type FormValues = z.infer<typeof formSchema>;

// Get the icon for a section type
const getSectionTypeIcon = (type: SectionType) => {
  switch (type) {
    case "banner":
      return <ImageIcon className="h-4 w-4" />;
    case "category":
      return <LibrarySquare className="h-4 w-4" />;
    case "book_summary":
      return <BookOpen className="h-4 w-4" />;
    case "collection":
      return <BookMarked className="h-4 w-4" />;
    case "people":
      return <Users className="h-4 w-4" />;
    default:
      return <LayoutPanelTop className="h-4 w-4" />;
  }
};

// Section Form component
const SectionForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isEditMode = !!id;

  // State
  const [isSaving, setIsSaving] = useState(false);

  // Fetch section for edit mode
  const { data: section, isLoading: isLoadingSection } = useQuery({
    queryKey: ["section", id],
    queryFn: () => fetchSectionById(id!),
    enabled: isEditMode,
  });

  // Fetch all sections to find the highest position (for new sections)
  const { data: sectionsData, isLoading: isLoadingSections } = useQuery({
    queryKey: ["sections-position"],
    queryFn: () =>
      fetchSections({
        pageSize: 100, // Increased to ensure we get all sections
        filters: { sortBy: "position", sortDirection: "desc" },
      }),
    enabled: !isEditMode,
  });

  // Calculate the highest current position for default
  const highestPosition = React.useMemo(() => {
    if (!sectionsData || !sectionsData.data || sectionsData.data.length === 0) {
      return 0;
    }

    // Find the highest position value among all sections
    return Math.max(...sectionsData.data.map((section) => section.position));
  }, [sectionsData]);

  // Form setup
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      subtitle: "",
      description: "",
      type: "book_summary", // Default type
      position: isEditMode ? 0 : highestPosition + 1, // Just add 1 to the highest position
      isActive: true,
      displayStyle: "grid", // Default style
      maxItems: undefined, // No default limit
    },
  });

  // Populate form when section data is available (for edit mode)
  useEffect(() => {
    if (section && isEditMode) {
      form.reset({
        title: section.title,
        subtitle: section.subtitle || "",
        description: section.description || "",
        type: section.type,
        position: section.position,
        isActive: section.isActive,
        displayStyle: section.displayStyle || "grid",
        maxItems: section.maxItems, // Keep the existing value or undefined
      });
    }
  }, [section, form, isEditMode]);

  // Update default position for new sections when sections data is loaded
  useEffect(() => {
    if (!isEditMode && !isLoadingSections && sectionsData) {
      // Use the highest position + 1 for new sections
      form.setValue("position", highestPosition + 1);
    }
  }, [sectionsData, isEditMode, form, highestPosition, isLoadingSections]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: saveSection,
    onSuccess: async (data) => {
      // Invalidate sections queries
      await queryClient.invalidateQueries({ queryKey: ["sections"] });

      if (isEditMode) {
        await queryClient.invalidateQueries({ queryKey: ["section", id] });
      }

      // Show success toast
      toast({
        title: `Section ${isEditMode ? "updated" : "created"} successfully`,
        description: `The section "${form.getValues().title}" has been ${
          isEditMode ? "updated" : "created"
        }.`,
      });

      // Navigate to section details page
      navigate(`/admin/sections/${isEditMode ? id : data.id}`);
    },
    onError: (error: Error) => {
      console.error("Error saving section:", error);
      toast({
        title: "Error saving section",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
      setIsSaving(false);
    },
    onSettled: () => {
      setIsSaving(false);
    },
  });

  // Form submission handler
  const onSubmit = (values: FormValues) => {
    setIsSaving(true);
    const saveData = {
      ...values,
      id: isEditMode ? id : undefined,
    };

    saveMutation.mutate(saveData);
  };

  // Loading state
  if (isEditMode && isLoadingSection) {
    return (
      <AdminLayout title={isEditMode ? "Edit Section" : "New Section"}>
        <div className="flex items-center justify-center h-full">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={isEditMode ? "Edit Section" : "New Section"}>
      <div className="mb-6">
        <Button
          variant="outline"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Sections
        </Button>
      </div>

      <div className="min-h-[calc(100vh-200px)]">
        <Card className="max-w-3xl mx-auto">
          <form onSubmit={form.handleSubmit(onSubmit)} className="relative">
            <CardHeader>
              <CardTitle>
                {isEditMode ? "Edit Section" : "Create New Section"}
              </CardTitle>
              <CardDescription>
                {isEditMode
                  ? "Update the section information below"
                  : "Fill out the form below to create a new section"}
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="space-y-4">
                {/* Section Title */}
                <div className="space-y-2">
                  <Label htmlFor="title">
                    Section Title <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="title"
                    placeholder="Enter section title"
                    {...form.register("title")}
                    className={
                      form.formState.errors.title ? "border-destructive" : ""
                    }
                  />
                  {form.formState.errors.title && (
                    <p className="text-destructive text-sm">
                      {form.formState.errors.title.message}
                    </p>
                  )}
                </div>

                {/* Section Subtitle */}
                <div className="space-y-2">
                  <Label htmlFor="subtitle">Subtitle</Label>
                  <Input
                    id="subtitle"
                    placeholder="Enter section subtitle (optional)"
                    {...form.register("subtitle")}
                  />
                  <p className="text-sm text-muted-foreground">
                    A short description or tagline for this section
                  </p>
                </div>

                {/* Section Type */}
                <div className="space-y-2">
                  <Label htmlFor="type">
                    Section Type <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={form.watch("type")}
                    onValueChange={(value: SectionType) =>
                      form.setValue("type", value)
                    }
                  >
                    <SelectTrigger id="type">
                      <SelectValue placeholder="Select a section type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="banner" className="flex items-center">
                        <div className="flex items-center">
                          <ImageIcon className="h-4 w-4 mr-2" /> Banners
                        </div>
                      </SelectItem>
                      <SelectItem value="category">
                        <div className="flex items-center">
                          <LibrarySquare className="h-4 w-4 mr-2" /> Categories
                        </div>
                      </SelectItem>
                      <SelectItem value="book_summary">
                        <div className="flex items-center">
                          <BookOpen className="h-4 w-4 mr-2" /> Books
                        </div>
                      </SelectItem>
                      <SelectItem value="collection">
                        <div className="flex items-center">
                          <BookMarked className="h-4 w-4 mr-2" /> Collections
                        </div>
                      </SelectItem>
                      <SelectItem value="people">
                        <div className="flex items-center">
                          <Users className="h-4 w-4 mr-2" /> People
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {form.formState.errors.type && (
                    <p className="text-destructive text-sm">
                      {form.formState.errors.type.message}
                    </p>
                  )}
                </div>

                {/* Position */}
                <div className="space-y-2">
                  <Label htmlFor="position" className="flex items-center gap-2">
                    <MoveVertical className="h-4 w-4" />
                    Display Position
                  </Label>
                  <Input
                    id="position"
                    type="number"
                    min="0"
                    placeholder={
                      isEditMode
                        ? "Current position"
                        : `Default: ${highestPosition + 1}`
                    }
                    {...form.register("position")}
                  />
                  <p className="text-sm text-muted-foreground">
                    Lower numbers appear first. Sections are displayed in
                    ascending order.
                  </p>
                  {form.formState.errors.position && (
                    <p className="text-destructive text-sm">
                      {form.formState.errors.position.message}
                    </p>
                  )}
                </div>

                {/* Display Style */}
                <div className="space-y-2">
                  <Label htmlFor="displayStyle">Display Style</Label>
                  <Select
                    value={form.watch("displayStyle") || "grid"}
                    onValueChange={(value) =>
                      form.setValue("displayStyle", value)
                    }
                  >
                    <SelectTrigger id="displayStyle">
                      <SelectValue placeholder="Select a display style" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="grid">Grid</SelectItem>
                      <SelectItem value="carousel">Carousel</SelectItem>
                      <SelectItem value="list">List</SelectItem>
                      <SelectItem value="featured">Featured</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">
                    How this section will be displayed on the frontend
                  </p>
                </div>

                {/* Max Items */}
                <div className="space-y-2">
                  <Label htmlFor="maxItems">Maximum Items</Label>
                  <Input
                    id="maxItems"
                    type="number"
                    min="1"
                    placeholder="Enter maximum number of items"
                    {...form.register("maxItems")}
                  />
                  <p className="text-sm text-muted-foreground">
                    Maximum number of items to display in this section (0 means
                    no limit)
                  </p>
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Enter section description (optional)"
                    className="min-h-[120px] resize-y"
                    {...form.register("description")}
                  />
                  <p className="text-sm text-muted-foreground">
                    A detailed description of what this section contains
                  </p>
                </div>

                {/* Active Status */}
                <div className="space-y-2">
                  <Label
                    htmlFor="is-active"
                    className="flex items-center gap-2"
                  >
                    Visibility Status
                  </Label>
                  <div className="flex items-center space-x-4 border rounded-md p-3">
                    <div className="flex items-center gap-2">
                      <Switch
                        id="is-active"
                        checked={form.watch("isActive")}
                        onCheckedChange={(checked) =>
                          form.setValue("isActive", checked)
                        }
                      />
                      <Label htmlFor="is-active" className="cursor-pointer">
                        {form.watch("isActive") ? "Active (Visible)" : "Hidden"}
                      </Label>
                    </div>
                    <div className="text-muted-foreground text-sm flex items-center ml-2">
                      {form.watch("isActive") ? (
                        <>
                          <Eye className="h-4 w-4 mr-2 text-green-500" />
                          <span>This section will be visible to users</span>
                        </>
                      ) : (
                        <>
                          <EyeOff className="h-4 w-4 mr-2" />
                          <span>This section will be hidden from users</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {isEditMode && (
                <div className="bg-muted/50 p-4 rounded-md">
                  <p className="text-sm font-medium mb-1">
                    Section Content Management
                  </p>
                  <p className="text-sm text-muted-foreground mb-3">
                    After saving this section, you can add and manage content
                    items in the section details.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(`/admin/sections/${id}`)}
                    className="text-sm"
                  >
                    Manage Section Content
                  </Button>
                </div>
              )}
            </CardContent>

            <CardFooter className="flex justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    {isEditMode ? "Update Section" : "Create Section"}
                  </>
                )}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default SectionForm;
